import { Site } from "@equiem/web-ng-lib";
import React, { createRef, useContext, useEffect, useState } from "react";
import { GoChevronLeft, GoChevronRight } from "react-icons/go";
import Swiper from "swiper";
import { Navigation, Pagination } from "swiper/modules";

Swiper.use([Navigation, Pagination]);

interface Props {
  images: string[];
  className?: string;
}

export const ImageSwiper: React.FC<Props> = ({
  images,
  className = "",
}) => {
  const site = useContext(Site);
  const [swiper, setSwiper] = useState<Swiper | undefined>();
  const [active, setActive] = useState(0);

  if (images.length === 0) {
    return null;
  }

  const container = createRef<HTMLDivElement>();
  const prevEl = createRef<HTMLAnchorElement>();
  const nextEl = createRef<HTMLAnchorElement>();

  const initOrUpdateSwiper = () => {
    const currentSwiper = swiper != null ? swiper : new Swiper(container.current!, {
      simulateTouch: false,
      watchSlidesProgress: true,
      effect: "fade",
      navigation: {
        nextEl: nextEl.current ?? undefined,
        prevEl: prevEl.current ?? undefined,
      },
      on: {
        slideChange: () => {
          setActive(currentSwiper.activeIndex);
        },
      },
    });

    if (swiper != null) {
      currentSwiper.update();
    }
    else {
      setSwiper(currentSwiper);
    }

    return currentSwiper;
  };

  useEffect(() => () => {
    if (swiper != null) {
      swiper.destroy(true, true);
    }
  }, []);

  useEffect(() => {
    initOrUpdateSwiper();
  }, [images]);

  return (
    <div className={`text-dark image-swiper ${className}`}>
      <div ref={container} className="swiper">
        <div className="swiper-wrapper">
          { images.map((image, i) => (
            <div key={i} className="swiper-slide"><img src={image} className="rounded" /></div>
          ))}
        </div>
      </div>
      <a ref={prevEl} className="prev rounded rounded-circle">
        <GoChevronLeft style={{ verticalAlign: "top", marginBottom: 0 }} />
      </a>
      <a ref={nextEl} className="next rounded rounded-circle">
        <GoChevronRight style={{ verticalAlign: "top", marginBottom: 0 }} />
      </a>
      { images.length < 2 ? null : (
        <div className="pagination mt-2 d-flex justify-content-center">
          { images.map((_image, i) => (
            <a key={i} onClick={() => swiper?.slideTo(i)} className="px-1">
              <span className={`page rounded rounded-circle ${i === active ? "active" : ""}`}> </span>
            </a>
          )) }
        </div>
      )}
      <style jsx>{`
      .image-swiper {
        position: relative;
      }
      img {
        width: 100%;
      }
      .pagination a {
        cursor: pointer;
        display: inline-block;
      }
      .pagination .page {
        display: inline-block;
        width: 0.875rem;
        height: 0.875rem;
        background: ${site.primaryColour};
        opacity: 0.5;
      }
      .pagination .page.active {
        opacity: 1;
      }
      .prev, .next {
        color: white;
        font-size: 2rem;
        position: absolute;
        top: 50%;
        margin-top: -1rem;
        width: 2rem;
        height: 2rem;
        z-index: 1;
        background: rgba(0,0,0,0.1);
      }
      .prev:hover,
      .prev:focus,
      .next:hover,
      .next:focus {
        outline: none;
        box-shadow: 0 4px 15px rgba(0,0,0,0.6);
        color: white;
      }
      .swiper-button-disabled,
      .swiper-button-disabled:hover,
      .swiper-button-disabled:focus {
        display: none;
      }
      .prev {
        left: .3rem;
      }
      .next {
        right: .3rem;
      }
      `}</style>
    </div>
  );
};
